import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Subscription } from "rxjs";

import { AuthModel } from "@intocare/auth-data";
import { PasswordPolicy } from "@intocare/interfaces";
import { SnackbarService } from "@intocare/ui";
import { hasLowerCase, hasMinLength, hasNumbers, hasSymbols, hasUpperCase } from "@intocare/validators";

import { PasswordValidator } from "../../validators/password-validator";

@Component({
	selector: "ic-confirm-password-form",
	templateUrl: "./confirm-password.component.html",
	styleUrls: ["./confirm-password.component.scss"],
	standalone: false,
})
export class ConfirmPasswordFormComponent implements OnInit, OnDestroy {
	private readonly authModel = inject(AuthModel);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly snackbarService = inject(SnackbarService);

	form: UntypedFormGroup;
	private errorMessage: string;
	user: string;

	passwordPolicy: PasswordPolicy = {
		MinimumLength: 8,
		RequireLowercase: true,
		RequireUppercase: true,
		RequireNumbers: true,
		RequireSymbols: true,
	};
	validationRules = [];
	validationChecksState: { key: string; active: boolean; value: undefined }[] = this.validationRules.map((key) => ({
		key,
		active: false,
		value: undefined,
	}));
	formSubscription: Subscription;
	passwordPolicySubscription: Subscription;
	loadingPasswordPolicy$ = new BehaviorSubject<boolean>(false);

	@Input() loading = true;
	@Output() authenticate = new EventEmitter();
	@Input()
	set error(value: string) {
		this.errorMessage = value;

		if (value) {
			this.form.get("password").reset();
		}
	}

	get error() {
		return this.errorMessage;
	}

	get invalidMatch() {
		return (
			this.form.get("newPassword").touched
			&& this.form.get("verifyPassword").touched
			&& this.form.hasError("passwordValidator")
		);
	}

	constructor() {
		this.form = new FormGroup(
			{
				newPassword: new FormControl("", {
					validators: [Validators.required],
				}),
				verifyPassword: new FormControl("", {
					validators: [Validators.required],
				}),
			},
			{
				validators: [PasswordValidator],
			},
		);
	}

	ngOnInit() {
		this.form.reset();
		const organisation = this.getOrganisation();

		this.loadingPasswordPolicy$.next(true);
		this.passwordPolicySubscription = this.authModel.getPasswordPolicy(organisation).subscribe({
			next: (policy) => {
				this.passwordPolicy = policy;
				this.validationRules = this.passwordPolicy
					? Object.entries(this.passwordPolicy)
							.map(([key, value]) => {
								if (value) {
									return key;
								}
							})
							.filter((x) => x)
					: [];
			},
			complete: () => {
				this.validationChecksState = this.validatePassword(this.form?.value?.credentials?.password ?? "").map(
					([key, value]) => ({
						key,
						active: value,
						value: this.passwordPolicy[key],
					}),
				);

				this.loadingPasswordPolicy$.next(false);
			},
			error: () => {
				this.loadingPasswordPolicy$.next(false);
				this.snackbarService.show({
					text: "user-settings.modules.password-policy.no-password-policy-found",
					error: true,
				});
				this.router.navigate(["select-organisation"]);
			},
		});

		this.formSubscription = this.form?.valueChanges?.subscribe(
			({ newPassword }: { newPassword?: string; confirmPassword?: string }) => {
				this.form.markAllAsTouched();
				this.validationChecksState = this.validatePassword(newPassword ?? "").map(([key, value]) => ({
					key,
					active: value,
					value: (this.passwordPolicy ?? {})[key],
				}));
			},
		);
	}

	ngOnDestroy(): void {
		this.formSubscription?.unsubscribe();
		this.passwordPolicySubscription?.unsubscribe();
	}

	hasPasswordPolicyError(): boolean {
		return this.validationChecksState.some(({ active }) => !active);
	}

	getOrganisation() {
		const parameterHQ = this.route.snapshot.paramMap.get("org");
		const parameterHome = this.route.snapshot.paramMap.get("home");
		return (
			window.localStorage.getItem("currentOrganisation")
			?? `${parameterHQ ?? ""}${parameterHome ? "." : ""}${parameterHome ?? ""}`.trim()
		);
	}

	validatePassword(password: string): [string, boolean][] {
		const errors: ValidationErrors = {};
		const validationSet = new Map<string, boolean>([
			[
				"MinimumLength",
				hasMinLength(password, { minlength: this.passwordPolicy?.MinimumLength })?.minlength.valid ?? true,
			],
			["RequireLowercase", hasLowerCase(password)?.lowercase.valid ?? true],
			["RequireUppercase", hasUpperCase(password)?.uppercase.valid ?? true],
			["RequireNumbers", hasNumbers(password)?.numbers.valid ?? true],
			["RequireSymbols", hasSymbols(password)?.symbols.valid ?? true],
		]);

		const validationResponse = [...validationSet].filter(([key]) => this.validationRules.includes(key));

		validationResponse.forEach(([key, value]) => {
			errors[key] = !value;
		});

		return validationResponse;
	}

	submit() {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			const passwordControl = this.form.get("credentials.newPassword");
			const confirmPasswordControl = this.form.get("credentials.verifyPassword");

			if (typeof passwordControl.value !== "string") {
				passwordControl.setValue("");
			}

			if (typeof confirmPasswordControl.value !== "string") {
				confirmPasswordControl.setValue("");
			}

			this.form.updateValueAndValidity();

			return;
		}

		if (!this.shouldSubmitButtonBeDisabled()) {
			const formValues = this.form.value;
			this.authenticate.emit(formValues);
		}
	}

	shouldSubmitButtonBeDisabled(): boolean {
		return (
			this.loading
			|| this.loadingPasswordPolicy$.value
			|| !!this.error
			|| this.hasPasswordPolicyError()
			|| this.invalidMatch
		);
	}

	hasRequiredError(control: string): boolean {
		const formElement = this.form.get(control);

		return formElement.touched && formElement.hasError("required");
	}
}
